import type { Platform, PostContext as Post } from '@rp/contracts'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { formatRelative } from '../../lib/time'
import { PLATFORM_LABELS } from '../queue/labels'
import { ITEM_COPY } from './copy'

type PostProps = { post: Post; platform: Platform; now: Date }

const placeholderOf = (post: Post): string | null => {
  if (post.status === 'pending') return ITEM_COPY.postPending
  if (post.status === 'unavailable') return ITEM_COPY.postUnavailable
  return post.text === null ? ITEM_COPY.postEmpty : null
}

const PostText = ({ text }: { text: string }) => {
  const textId = useId()
  const ref = useRef<HTMLParagraphElement>(null)
  const [cutShort, setCutShort] = useState(false)
  const [expanded, setExpanded] = useState(false)
  useLayoutEffect(() => {
    const element = ref.current
    if (element !== null) setCutShort(element.scrollHeight > element.clientHeight)
  }, [text])
  return (
    <>
      <p ref={ref} id={textId} className={expanded ? 'post-body' : 'post-body post-body-clamped'}>
        {text}
      </p>
      {cutShort || expanded ? (
        <button
          type="button"
          className="post-toggle"
          aria-expanded={expanded}
          aria-controls={textId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? ITEM_COPY.postCollapse : ITEM_COPY.postExpand}
        </button>
      ) : null}
    </>
  )
}

const PostMeta = ({ post, platform, now }: PostProps) => {
  if (post.postedAt === null && post.permalinkUrl === null) return null
  return (
    <p className="post-meta">
      {post.postedAt === null ? null : (
        <span className="num">{ITEM_COPY.postPostedAt(formatRelative(new Date(post.postedAt), now))}</span>
      )}
      {post.permalinkUrl === null ? null : (
        <a className="post-link" href={post.permalinkUrl} target="_blank" rel="noopener noreferrer">
          {ITEM_COPY.postOpen(PLATFORM_LABELS[platform])}
          <span className="visually-hidden">{ITEM_COPY.opensInNewTab}</span>
        </a>
      )}
    </p>
  )
}

export const PostContext = (props: PostProps) => {
  const headingId = useId()
  const placeholder = placeholderOf(props.post)
  return (
    <section className="post-context" aria-labelledby={headingId}>
      <h2 id={headingId} className="thread-label">
        {ITEM_COPY.post}
      </h2>
      {placeholder !== null || props.post.text === null ? (
        <p className="post-placeholder">{placeholder}</p>
      ) : (
        <PostText text={props.post.text} />
      )}
      <PostMeta {...props} />
    </section>
  )
}
